import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send } from "lucide-react";

export const BookingMessages = ({ booking, open, onOpenChange, counterpart }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    if (!booking) return;
    try {
      const { data } = await api.get(`/bookings/${booking.id}/messages`);
      setMessages(data);
    } catch {
      /* ignore */
    }
  }, [booking]);

  useEffect(() => {
    if (!open) return;
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [open, load]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/messages`, { body });
      setMessages((m) => [...m, data]);
      setText("");
    } catch {
      toast.error("Could not send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden" data-testid="messages-dialog">
        <DialogHeader className="px-5 py-4 border-b border-neutral-200">
          <DialogTitle className="font-display font-black tracking-tight text-lg">
            {counterpart || "Messages"}
          </DialogTitle>
          <div className="text-xs text-neutral-500 truncate">
            {booking?.listing_title} · {booking?.date} · {booking?.start_time}–{booking?.end_time}
          </div>
        </DialogHeader>

        <div
          ref={scrollRef}
          className="h-80 overflow-y-auto px-5 py-4 space-y-3 bg-neutral-50"
          data-testid="messages-list"
        >
          {messages.length === 0 ? (
            <div className="text-sm text-neutral-400 text-center pt-10">
              No messages yet. Sort out load-in, keys, and quiet hours.
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.sender_id === user?.id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                      mine ? "bg-[#FF4500] text-white" : "bg-white border border-neutral-200"
                    }`}
                    data-testid="message-bubble"
                  >
                    {!mine && <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-0.5">{m.sender_name}</div>}
                    {m.body}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="p-3 border-t border-neutral-200 flex items-center gap-2">
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Type a message…"
            data-testid="message-input"
          />
          <Button
            onClick={send}
            disabled={sending || !text.trim()}
            className="bg-[#FF4500] hover:bg-[#E63E00] text-white rounded-full h-10 w-10 p-0 shrink-0"
            data-testid="message-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingMessages;
